// Every automated and staff decision, with what it was about in words and how often staff agreed
// with Claude. Withdrawal rows open the review drawer; flag rows open the user.
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Bot, Hand } from 'lucide-react';
import { api } from '../lib/api';
import { flagLabel } from '../lib/risk';
import {
  actorLabel,
  EmptyState,
  ErrorNote,
  fmtMoney,
  fmtNum,
  fmtUsd,
  PageHeader,
  Pill,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
} from '../components/ui';

interface Decision {
  id: string;
  subjectType: string;
  subjectId: string;
  action: string;
  actor: string;
  mode: string;
  confidence: number | null;
  reasoning: string | null;
  outcome: string | null;
  createdAt: number;
  withdrawal: {
    id: string;
    status: string;
    coins: number;
    amountMinor: number;
    item: { amount: number; productName: string; productCurrency: string | null } | null;
    userId: string;
    username: string;
  } | null;
  flag: { id: string; code: string; status: string; userId: string; username: string } | null;
}

const ACTION: Record<
  string,
  { label: string; tone: 'mint' | 'coin' | 'berry' | 'neutral' | 'sky' }
> = {
  approve_withdrawal: { label: 'Approve', tone: 'mint' },
  hold_withdrawal: { label: 'Hold', tone: 'coin' },
  reject_withdrawal: { label: 'Reject', tone: 'berry' },
  recommend_dismiss: { label: 'Dismiss flag', tone: 'neutral' },
  recommend_monitor: { label: 'Monitor', tone: 'sky' },
  recommend_restrict: { label: 'Restrict user', tone: 'berry' },
  recommend_escalate: { label: 'Escalate', tone: 'coin' },
  no_recommendation: { label: 'No call', tone: 'neutral' },
};
const sentence = (t: string) => {
  const x = t.replace(/_/g, ' ');
  return x.charAt(0).toUpperCase() + x.slice(1);
};
const actionOf = (a: string) => {
  // Flag decisions on linked old accounts read "recommend_x+legacy_y".
  const [base, extra] = a.split('+');
  const known = ACTION[base!] ?? {
    label: sentence(base!.replace(/^recommend_/, '')),
    tone: 'neutral' as const,
  };
  return extra ? { ...known, label: `${known.label} · ${extra.replace(/_/g, ' ')}` } : known;
};

/** Did staff end up doing what this decision said? */
function agreed(d: Decision): boolean | null {
  if (!d.outcome?.startsWith('staff_')) return null;
  const o = d.outcome.slice(6);
  if (d.subjectType === 'withdrawal') return d.action === `${o}_withdrawal`;
  const lenient = /^recommend_(dismiss|monitor)/.test(d.action);
  return lenient ? o === 'dismissed' : o === 'confirmed';
}

export function Decisions() {
  const [actor, setActor] = useState<'ai' | 'staff' | 'all'>('ai');
  const [subject, setSubject] = useState<'all' | 'withdrawal' | 'risk_flag'>('all');
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['decisions', actor, subject],
    queryFn: () =>
      api<{
        decisions: Decision[];
        staffNames: Record<string, string>;
        ai30d: { runs: number; costUsd: number };
      }>(`/decisions?actor=${actor}&subject=${subject}`),
    refetchInterval: 20_000,
    placeholderData: (p) => p,
  });
  const list = data?.decisions ?? [];
  const reviewed = list.map(agreed).filter((x) => x !== null);
  const agree = reviewed.filter(Boolean).length;
  const counts = (a: string) => list.filter((d) => d.action === a).length;
  const who = actor === 'ai' ? 'Claude' : actor === 'staff' ? 'staff' : 'anyone';

  return (
    <div className="space-y-5">
      <PageHeader
        title="Decisions"
        description="What Claude and staff decided, and why. When staff later act on the same case, the outcome shows whether they agreed with Claude."
      />
      {actor === 'ai' && data && list.length > 0 && (
        <section aria-label="Claude's calls in this list" className="card overflow-hidden">
          {/* gap-px over a hairline background draws the dividers at every column count. */}
          <div className="grid grid-cols-2 gap-px bg-hairline lg:grid-cols-4">
            <Stat label="Approved" value={counts('approve_withdrawal')} dot="bg-mint" />
            <Stat label="Held for staff" value={counts('hold_withdrawal')} dot="bg-coin" />
            <Stat label="Rejected" value={counts('reject_withdrawal')} dot="bg-berry" />
            <Stat
              label="Staff agreed"
              value={reviewed.length ? `${Math.round((agree / reviewed.length) * 100)}%` : '—'}
              sub={
                reviewed.length
                  ? `${agree} of ${reviewed.length} checked cases`
                  : 'No staff follow-ups yet'
              }
            />
          </div>
          <div className="border-t border-hairline px-4 py-2.5 text-[12px] text-stone sm:px-5">
            Counts cover the {fmtNum(list.length)} decision{list.length === 1 ? '' : 's'} below ·{' '}
            {fmtUsd(data.ai30d.costUsd)} model cost in the last 30 days
          </div>
        </section>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-2">
        <Tabs
          label="Who decided"
          value={actor}
          onChange={setActor}
          items={[
            { key: 'ai', label: 'Claude' },
            { key: 'staff', label: 'Staff' },
            { key: 'all', label: 'Everyone' },
          ]}
        />
        <Tabs
          label="Kind of case"
          value={subject}
          onChange={setSubject}
          items={[
            { key: 'all', label: 'All cases' },
            { key: 'withdrawal', label: 'Cash-outs' },
            { key: 'risk_flag', label: 'Risk flags' },
          ]}
        />
      </div>
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {error && !data ? null : (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : list.length === 0 ? (
            <EmptyState icon={Bot} title={`No decisions by ${who} yet`}>
              {actor === 'staff'
                ? 'Approvals, holds, rejections and flag calls made by staff show up here.'
                : 'Claude reviews each new cash-out and risk flag; they appear here.'}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {list.map((d) => {
                const a = actionOf(d.action);
                const ag = agreed(d);
                const isAi = d.actor.startsWith('ai:');
                return (
                  <li
                    key={d.id}
                    className="grid grid-cols-[28px_minmax(0,1fr)_auto] gap-x-3 px-4 py-4 sm:px-5"
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-canvas">
                      {isAi ? (
                        <Bot size={14} aria-hidden="true" />
                      ) : (
                        <Hand size={14} aria-hidden="true" />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
                      <span className="text-[14px] font-bold">
                        {actorLabel(d.actor, data?.staffNames)}
                      </span>
                      <Pill tone={a.tone}>{a.label}</Pill>
                      {d.confidence !== null && (
                        <span className="text-[12px] font-semibold tabular-nums text-stone">
                          {Math.round(d.confidence * 100)}% sure
                        </span>
                      )}
                      {d.mode === 'shadow' && <Pill>Shadow</Pill>}
                      {ag !== null && (
                        <Pill tone={ag ? 'mint' : 'berry'}>
                          {ag ? 'Staff agreed' : `Staff ${d.outcome!.slice(6)}`}
                        </Pill>
                      )}
                    </div>
                    <span className="pt-1 text-[12px] text-stone">
                      <TimeAgo t={d.createdAt} />
                    </span>
                    <div className="col-span-3 min-w-0 sm:col-span-1 sm:col-start-2">
                      <Subject d={d} />
                      {d.reasoning && (
                        <p className="mt-1.5 max-w-3xl text-[14px] leading-relaxed text-graphite">
                          {d.reasoning}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Subject({ d }: { d: Decision }) {
  if (d.withdrawal) {
    const w = d.withdrawal;
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <Link
          to={`/withdrawals?tab=all&id=${w.id}`}
          className="font-semibold underline decoration-coin decoration-2 underline-offset-4"
        >
          {w.item
            ? `${fmtMoney(w.item.amount, w.item.productCurrency ?? 'USD')} ${w.item.productName}`
            : `${fmtUsd(w.amountMinor / 100)} cash-out`}
        </Link>
        <span className="text-stone">for</span>
        <Link
          to={`/users/${w.userId}`}
          className="font-semibold hover:underline hover:decoration-coin hover:decoration-2 hover:underline-offset-4"
        >
          {w.username}
        </Link>
        <StatusPill status={w.status} />
      </div>
    );
  }
  if (d.flag) {
    const f = d.flag;
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <span className="font-semibold">Flag: {flagLabel(f.code)}</span>
        <span className="text-stone">on</span>
        <Link
          to={`/users/${f.userId}`}
          className="font-semibold underline decoration-coin decoration-2 underline-offset-4"
        >
          {f.username}
        </Link>
        <StatusPill status={f.status} />
      </div>
    );
  }
  return <div className="mt-1.5 text-[13px] text-stone">{d.subjectId}</div>;
}

function Stat({
  label,
  value,
  sub,
  dot,
}: {
  label: string;
  value: number | string;
  sub?: string;
  /** Colour key matching the action pills below. */
  dot?: string;
}) {
  return (
    <div className="bg-paper px-4 py-4 sm:px-5">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-stone">
        {dot && <span aria-hidden="true" className={`h-2 w-2 rounded-full ${dot}`} />}
        {label}
      </div>
      <div className="mt-1.5 text-[28px] font-extrabold leading-none tracking-[-0.025em] tabular-nums">
        {value}
      </div>
      {sub && <div className="mt-1.5 text-[12px] text-stone">{sub}</div>}
    </div>
  );
}
